import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  DollarSign,
  Users,
  Building2,
  Flame,
  AlertTriangle,
  Loader2,
  Download,
  FileText,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { exportMetricsCsv, exportMetricsPdf } from "@/lib/bi-export";

interface PlatformMetrics {
  active_mrr_cents: number;
  paying_agencies: number;
  trialing_agencies: number;
  churned_30d: number;
  new_subscriptions_30d: number;
  total_agencies: number;
  total_users: number;
  total_leads: number;
  hot_leads: number;
  leads_last_30d: number;
  computed_at: string;
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatNumber(n: number) {
  return new Intl.NumberFormat("en-US").format(n);
}

export default function BusinessIntelligence() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.functions.invoke<PlatformMetrics>(
      "calculate-platform-metrics",
    );
    if (error) {
      setError(error.message);
    } else if (data) {
      setMetrics(data);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const churnRate =
    metrics && metrics.paying_agencies > 0
      ? (metrics.churned_30d / metrics.paying_agencies) * 100
      : 0;

  const arpaCents =
    metrics && metrics.paying_agencies > 0
      ? Math.round(metrics.active_mrr_cents / metrics.paying_agencies)
      : 0;

  const churnRateForExport =
    metrics && metrics.paying_agencies > 0
      ? metrics.churned_30d / metrics.paying_agencies
      : 0;

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Admin
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Business Intelligence
              </h1>
              <p className="text-sm text-muted-foreground">
                Real-time platform health, MRR, and growth metrics
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!metrics || loading}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => metrics && exportMetricsCsv(metrics, { arpaCents, churnRate: churnRateForExport })}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Download CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => metrics && exportMetricsPdf(metrics, { arpaCents, churnRate: churnRateForExport })}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Download PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={load} disabled={loading} variant="outline" size="sm">
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>Failed to load metrics: {error}</span>
          </div>
        )}

        {loading && !metrics ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : metrics ? (
          <>
            <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                title="Active MRR"
                value={formatCurrency(metrics.active_mrr_cents)}
                icon={<DollarSign className="h-4 w-4" />}
                tone="primary"
              />
              <KpiCard
                title="Paying Agencies"
                value={formatNumber(metrics.paying_agencies)}
                icon={<Building2 className="h-4 w-4" />}
              />
              <KpiCard
                title="ARPA (avg)"
                value={formatCurrency(arpaCents)}
                icon={<TrendingUp className="h-4 w-4" />}
              />
              <KpiCard
                title="30-day Churn"
                value={`${churnRate.toFixed(1)}%`}
                subtitle={`${metrics.churned_30d} canceled`}
                icon={<AlertTriangle className="h-4 w-4" />}
                tone={churnRate > 5 ? "danger" : "default"}
              />
            </section>

            <section className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                title="Trialing Agencies"
                value={formatNumber(metrics.trialing_agencies)}
                icon={<Users className="h-4 w-4" />}
              />
              <KpiCard
                title="New Subs (30d)"
                value={formatNumber(metrics.new_subscriptions_30d)}
                icon={<TrendingUp className="h-4 w-4" />}
              />
              <KpiCard
                title="Total Agencies"
                value={formatNumber(metrics.total_agencies)}
                icon={<Building2 className="h-4 w-4" />}
              />
              <KpiCard
                title="Total Users"
                value={formatNumber(metrics.total_users)}
                icon={<Users className="h-4 w-4" />}
              />
            </section>

            <section className="mt-8">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Lead Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 md:grid-cols-3">
                    <Stat
                      label="Total Leads"
                      value={formatNumber(metrics.total_leads)}
                      icon={<Users className="h-4 w-4 text-muted-foreground" />}
                    />
                    <Stat
                      label="Hot Leads (≥85)"
                      value={formatNumber(metrics.hot_leads)}
                      icon={<Flame className="h-4 w-4 text-orange-500" />}
                    />
                    <Stat
                      label="Leads (last 30d)"
                      value={formatNumber(metrics.leads_last_30d)}
                      icon={<TrendingUp className="h-4 w-4 text-primary" />}
                    />
                  </div>
                </CardContent>
              </Card>
            </section>

            <p className="mt-6 text-xs text-muted-foreground">
              Last computed:{" "}
              {new Date(metrics.computed_at).toLocaleString()}
            </p>
          </>
        ) : null}
      </main>
    </div>
  );
}

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  tone = "default",
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon?: React.ReactNode;
  tone?: "default" | "primary" | "danger";
}) {
  const toneClass =
    tone === "primary"
      ? "border-primary/30 bg-primary/5"
      : tone === "danger"
        ? "border-destructive/30 bg-destructive/5"
        : "";
  return (
    <Card className={toneClass}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{title}</span>
          {icon}
        </div>
        <div className="mt-2 text-2xl font-semibold">{value}</div>
        {subtitle && (
          <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}